/**
 * Meta-Daten der Anzeigen-Landingpages — die eine Quelle für Helmet UND den Build.
 *
 * WARUM (18.09.2026): index.html ist eine Shell für alle Routen. Title, Description,
 * og:url und JSON-LD darin sind die Startseiten-Variante; die Route-Meta entstand
 * bisher erst nach dem JS-Bundle in den <Helmet>-Blöcken von GassiServiceRouter.tsx (heute GassiServiceA.tsx)
 * und Hundetagesstaette.tsx. Überspringt Google die Render-Warteschlange, sieht es
 * die Startseite und wählt „/" als Canonical (GSC-Audit 16.09.: jede neue Seite ein
 * Münzwurf). Das Vite-Plugin `charlies-route-html` (vite.config.ts) schreibt deshalb
 * beim Build je Route eine eigene index.html mit fertigem Head. Damit Route-HTML und
 * Helmet nie auseinanderlaufen, lesen beide aus dieser Datei.
 *
 * REGELN FÜR DIESE DATEI
 * - Kein React, keine Browser-APIs, keine `@/`-Aliase. vite.config.ts importiert sie
 *   relativ (`./src/lib/lpMeta`); esbuild bündelt sie beim Laden der Config in Node.
 *   Ein Alias würde dort nicht aufgelöst, `window` gibt es dort nicht.
 * - Nur Literale und Imports aus ebenso reinen Dateien (z. B. daycareStandort.ts). Kein
 *   Datum, kein Zufall: 15 Publishes am Tag müssen identisches HTML liefern.
 * - Hier steht nur, was je Route abweicht. Was für alle Seiten gilt (og:image,
 *   twitter:card, LocalBusiness-JSON-LD der Firma), bleibt in index.html und wird
 *   von Helmet wie vom Plugin unangetastet gelassen.
 */

import { DAYCARE_STREET, eroeffnungKurz } from "./daycareStandort";
import { DATENSCHUTZ_STAND_ISO } from "./datenschutzStand";

export interface LpRouteMeta {
  /** Pfad ohne Slash am Ende — Schlüssel für Plugin (Ausgabeordner) und Seiten. */
  /** Statische Route; Blog-Artikel tragen hier ihren eigenen Pfad `/blog/<slug>`. */
  pfad: string;
  title: string;
  description: string;
  canonical: string;
  og: { type: string; title: string; description: string; url: string };
  twitter: { title: string; description: string };
  /** Optional (z. B. "noindex"). Heute nur /datenschutz (noindex,follow); index.html setzt keins. */
  robots?: string;
  /** Sprache der Seite fürs <html lang> im Roh-HTML, wenn sie nicht Deutsch ist (heute nur /en). */
  lang?: string;
  /** hreflang-Paare der Seite (heute nur /en; die Startseite nutzt HREFLANG_START_EN direkt). */
  alternates?: HreflangLink[];
  /** Route-eigenes JSON-LD — kommt ZUSÄTZLICH zum LocalBusiness-Block aus index.html. */
  jsonLd: Record<string, unknown>;
  /**
   * true = die Seite rendert GENAU dieses jsonLd auch per Helmet. Das Plugin markiert den
   * Roh-HTML-Block dann mit `data-rh="true"`, sodass Helmet ihn nach dem Laden als eigenen
   * erkennt und übernimmt, statt einen zweiten, identischen Block anzuhängen. Nötig, sobald
   * das JSON-LD ein FAQPage enthält: zwei FAQPage-Blöcke im gerenderten DOM meldet Google
   * als Fehler („Doppeltes Feld FAQPage“). Nur setzen, wenn die Seite dasselbe jsonLd per
   * Helmet ausgibt, sonst entfernt Helmet den Block nach dem Laden.
   */
  jsonLdVonHelmetUebernommen?: boolean;
}

/** Ein <link rel="alternate" hreflang="…">. */
export interface HreflangLink {
  hreflang: string;
  href: string;
}

/**
 * hreflang zwischen der Startseite und der englischen Kurzseite /en (GEO-Maßnahme 12,
 * 12.10.2026). Beide Seiten tragen dieselben drei Tags (gegenseitig, sonst ignoriert Google
 * sie): Index.tsx und En.tsx per Helmet, das Plugin `charlies-route-html` im Roh-HTML.
 * x-default = die deutsche Startseite.
 */
export const HREFLANG_START_EN: HreflangLink[] = [
  { hreflang: "de", href: "https://www.charliescrew.com/" },
  { hreflang: "en", href: "https://www.charliescrew.com/en" },
  { hreflang: "x-default", href: "https://www.charliescrew.com/" },
];

export type LpMetaTag =
  | { name: string; content: string }
  | { property: string; content: string };

/**
 * Alle <meta>-Tags einer Route in fester Reihenfolge. Helmet (meta-Prop) und das
 * Build-Plugin nehmen dieselbe Liste — so gibt es genau eine Stelle, die entscheidet,
 * welche Tags eine Landingpage setzt.
 */
export function lpMetaTags(m: LpRouteMeta): LpMetaTag[] {
  const tags: LpMetaTag[] = [
    { name: "description", content: m.description },
    { property: "og:type", content: m.og.type },
    { property: "og:title", content: m.og.title },
    { property: "og:description", content: m.og.description },
    { property: "og:url", content: m.og.url },
    { name: "twitter:title", content: m.twitter.title },
    { name: "twitter:description", content: m.twitter.description },
  ];
  if (m.robots) tags.push({ name: "robots", content: m.robots });
  return tags;
}

// /gassi-service: Beide A/B-Varianten rendern dieselbe Seite unter derselben URL und
// MÜSSEN deshalb einen gemeinsamen Title/Canonical haben — sonst sieht Google für eine
// URL wechselnde Meta.
const GASSI_URL = "https://www.charliescrew.com/gassi-service";

const gassiService: LpRouteMeta = {
  pfad: "/gassi-service",
  // ENTWURF LOOP-2 KW45 (03.11.2026), Freigabe Copy im PR. GSC 04.10.-01.11.: „gassi service
  // münchen“ Pos. 11,5 (83 Impr., 0 Klicks), „gassiservice münchen“ Pos. 8,9, „gassiservice“ 26 Impr.
  // Alter Title 68 Zeichen, alte Description 199 Zeichen: Google schnitt beide ab, der Handlungs-
  // aufruf wurde nie gezeigt. Neu: Title 60, Description 154 Zeichen, Abholung (stärkstes Argument)
  // nach vorn, Schreibweise „Gassiservice“ einmal mit drin, keine Gruppengröße.
  title: "Gassi-Service München: Abholung, Wald & Park | charlies crew",
  description:
    "Gassiservice in München: Wir holen deinen Hund ab, am Vor- oder Nachmittag ist er gut zwei Stunden in Wald und Park unterwegs. Voll versichert, mit Fotos.",
  canonical: GASSI_URL,
  og: {
    type: "website",
    title: "Gassi-Service München - Walks in Kleingruppen | charlies crew",
    description:
      "Gassi-Service in München: unterwegs im Rudel in Wald und Park, mit Abholung, voll versichert. Jetzt Schnupper-Telefonat vereinbaren.",
    url: GASSI_URL,
  },
  twitter: {
    title: "Gassi-Service München - Walks in Kleingruppen | charlies crew",
    description:
      "Gassi-Service in München: unterwegs im Rudel in Wald und Park, mit Abholung, voll versichert.",
  },
  // 30.09. bis 11.10.2026 ein @graph aus Service + FAQPage. Seit 11.10.2026 (Noah) steht auf der
  // Gassi-LP keine FAQ mehr, deshalb auch kein FAQPage-Schema: strukturierte Daten dürfen nur
  // zeigen, was sichtbar ist. Die Fragen (Leine, Stadtteile) beantworten künftig die Stadtteilseiten.
  // Seit LOOP-2 (14.10.2026) steht wieder eine FAQ unter dem Preisbereich. Ihr FAQPage-Block kommt
  // NICHT hierher (Chunk app-shared), sondern aus gassiFaqJsonLd() in seiteGassiService.ts:
  // per Helmet in GassiServiceA.tsx und im Roh-HTML über rohRouten.ts.
  jsonLd: {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: "Gassi-Service / Dog Walking",
    name: "Gassi-Service München - charlies crew",
    description:
      "Gassi-Service in München: Hundebetreuung in kleinen Gruppen in Wald und Park, mit Abholung, voll versichert.",
    areaServed: { "@type": "City", name: "München" },
    provider: {
      "@type": "LocalBusiness",
      "@id": "https://www.charliescrew.com/#business",
      name: "charlies crew",
    },
    url: GASSI_URL,
  },
  // GassiServiceA.tsx gibt genau dieses jsonLd auch per Helmet aus.
  jsonLdVonHelmetUebernommen: true,
};

// /hundetagesstaette: Adresse und Eröffnung kommen aus daycareStandort.ts, damit Badge,
// Meta-Description und JSON-LD nicht wieder auseinanderlaufen (Founder-Entscheid 26.08.2026).
const HUTA_URL = "https://www.charliescrew.com/hundetagesstaette";
const HUTA_TITLE = "Hundetagesstätte München-Pasing | charlies crew";
const HUTA_SOCIAL_DESCRIPTION =
  `Hundetagesstätte in München-Pasing, ${DAYCARE_STREET}: kleine Gruppen, erfahrenes Team, feste Stammplätze. ${eroeffnungKurz()}. Platz auf der Warteliste sichern!`;

const hundetagesstaette: LpRouteMeta = {
  pfad: "/hundetagesstaette",
  title: HUTA_TITLE,
  description:
    `Hundetagesstätte in München-Pasing, ${DAYCARE_STREET}: kleine Gruppen, erfahrenes Team, feste Stammplätze. ${eroeffnungKurz()}. Platz auf der Warteliste sichern!`,
  canonical: HUTA_URL,
  og: {
    type: "website",
    title: HUTA_TITLE,
    description: HUTA_SOCIAL_DESCRIPTION,
    url: HUTA_URL,
  },
  twitter: {
    title: HUTA_TITLE,
    description: HUTA_SOCIAL_DESCRIPTION,
  },
  // Bis 12.10.2026 stand hier der LocalBusiness-Knoten der Tagesstätte selbst. Seit GEO-Maßnahme 9
  // steht er (mit Öffnungszeiten und „ab“-Preisen) im Firmen-Schema im Roh-HTML JEDER Seite
  // (src/lib/hutaSchema.ts über firmaSchema.ts), auch dieser. Hier nur noch der Verweis per @id,
  // damit die Seite nicht zwei unterschiedlich ausführliche Knoten mit derselben @id trägt.
  // Die Preise dürfen nicht in diese Datei: lpMeta liegt im Chunk app-shared, den jede Seite lädt.
  jsonLd: {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${HUTA_URL}#webpage`,
    name: HUTA_TITLE,
    description:
      `Hundetagesstätte in München-Pasing: feste Stammplätze in kleinen Gruppen, Indoor- und Outdoor-Bereich. ${eroeffnungKurz()}.`,
    url: HUTA_URL,
    isPartOf: { "@type": "WebSite", "@id": "https://www.charliescrew.com/#website" },
    about: { "@id": "https://www.charliescrew.com/hundetagesstaette#huta" },
    mainEntity: { "@id": "https://www.charliescrew.com/hundetagesstaette#huta" },
  },
  // Hundetagesstaette.tsx gibt genau dieses jsonLd auch per Helmet aus (ein Block statt zwei).
  jsonLdVonHelmetUebernommen: true,
};

// Ab 21.09.2026 auch die übrigen Geldseiten. Vorher lieferten sie im Roh-HTML den
// Startseiten-Head OHNE Canonical — live nachgewiesen für /preise, /faq und alle
// Blog-Artikel. Für einen Crawler, der nicht rendert, sahen sie damit alle gleich aus.

const PREISE_URL = "https://www.charliescrew.com/preise";
const PREISE_TITLE = "Preise Hundebetreuung München | charlies crew";
// Bewusst ohne konkreten Betrag: lpMeta.ts muss frei von Alias-Importen bleiben
// (vite.config.ts lädt sie relativ), und ein Preis im Head veraltet ohnehin schnell.
const PREISE_DESCRIPTION =
  "Alle Preise für Gassi-Service und Hundetagesstätte in München auf einen Blick: Karten, Einstieg und was jeweils enthalten ist.";

const preise: LpRouteMeta = {
  pfad: "/preise",
  title: PREISE_TITLE,
  description: PREISE_DESCRIPTION,
  canonical: PREISE_URL,
  og: { type: "website", title: PREISE_TITLE, description: PREISE_DESCRIPTION, url: PREISE_URL },
  twitter: { title: PREISE_TITLE, description: PREISE_DESCRIPTION },
  jsonLd: {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: PREISE_TITLE,
    description: PREISE_DESCRIPTION,
    url: PREISE_URL,
    isPartOf: { "@type": "WebSite", "@id": "https://www.charliescrew.com/#website" },
  },
};

const FAQ_URL = "https://www.charliescrew.com/faq";
const FAQ_TITLE = "Häufige Fragen zur Hundebetreuung in München";
const FAQ_DESCRIPTION =
  "Wie läuft ein Walk ab, was kostet er, wie sicher ist dein Hund unterwegs? Die Antworten auf die Fragen, die uns am häufigsten erreichen.";

const faq: LpRouteMeta = {
  pfad: "/faq",
  title: FAQ_TITLE,
  description: FAQ_DESCRIPTION,
  canonical: FAQ_URL,
  og: { type: "website", title: FAQ_TITLE, description: FAQ_DESCRIPTION, url: FAQ_URL },
  twitter: { title: FAQ_TITLE, description: FAQ_DESCRIPTION },
  // Das FAQPage-Schema mit allen Fragen setzt die Seite selbst per Helmet — hier nur
  // die WebPage-Auszeichnung, damit das Roh-HTML nicht ohne Kontext dasteht.
  jsonLd: {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: FAQ_TITLE,
    description: FAQ_DESCRIPTION,
    url: FAQ_URL,
    isPartOf: { "@type": "WebSite", "@id": "https://www.charliescrew.com/#website" },
  },
};

const SITTER_URL = "https://www.charliescrew.com/hundesitter-muenchen";
const SITTER_TITLE = "Hundesitter München mit Abholung | charlies crew";
const SITTER_DESCRIPTION =
  "Hundesitter in München: wir holen deinen Hund zu Hause ab, er ist im Rudel in Wald und Park unterwegs, und wir bringen ihn zurück. Voll versichert.";

const hundesitterMuenchen: LpRouteMeta = {
  pfad: "/hundesitter-muenchen",
  title: SITTER_TITLE,
  description: SITTER_DESCRIPTION,
  canonical: SITTER_URL,
  og: { type: "website", title: SITTER_TITLE, description: SITTER_DESCRIPTION, url: SITTER_URL },
  twitter: { title: SITTER_TITLE, description: SITTER_DESCRIPTION },
  jsonLd: {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: "Hundesitting / Hundebetreuung",
    name: SITTER_TITLE,
    description: SITTER_DESCRIPTION,
    areaServed: { "@type": "City", name: "München" },
    provider: {
      "@type": "LocalBusiness",
      "@id": "https://www.charliescrew.com/#business",
      name: "charlies crew",
    },
    url: SITTER_URL,
  },
};

const FINDER_URL = "https://www.charliescrew.com/welcher-service";
const FINDER_TITLE = "Gassi-Service oder Hundetagesstätte? Der Service-Finder";
const FINDER_DESCRIPTION =
  "Zehn Sätze, eine Minute: und du weißt, ob unser Gassi-Service oder die Hundetagesstätte in Pasing zu deinem Hund passt.";

const serviceFinder: LpRouteMeta = {
  pfad: "/welcher-service",
  title: FINDER_TITLE,
  description: FINDER_DESCRIPTION,
  canonical: FINDER_URL,
  og: { type: "website", title: FINDER_TITLE, description: FINDER_DESCRIPTION, url: FINDER_URL },
  twitter: { title: FINDER_TITLE, description: FINDER_DESCRIPTION },
  jsonLd: {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: FINDER_TITLE,
    description: FINDER_DESCRIPTION,
    url: FINDER_URL,
    isPartOf: { "@type": "WebSite", "@id": "https://www.charliescrew.com/#website" },
  },
};

// Ab 30.09.2026 auch die übrigen Seiten aus der Sitemap. Bis dahin lieferten /services,
// /about, /schnuppertag, /jobs und /blog im Roh-HTML den Startseiten-Head ohne Canonical
// (live per curl: gleicher md5 wie „/"). Damit hat jetzt jede indexierbare Route außer
// der Startseite selbst ihren eigenen Head — die Startseite IST die Shell.

const SERVICES_URL = "https://www.charliescrew.com/services";
const SERVICES_TITLE = "Unsere Leistungen für deinen Hund in München | charlies crew";
const SERVICES_SOCIAL_DESCRIPTION =
  "Gassi-Service, Hundesitter und Hundetagesstätte in München - alle Leistungen von charlies crew im Überblick.";
const SERVICE_PROVIDER = {
  "@type": "LocalBusiness",
  "@id": "https://www.charliescrew.com/#business",
  name: "charlies crew",
};

const services: LpRouteMeta = {
  pfad: "/services",
  title: SERVICES_TITLE,
  description:
    "Alle Leistungen von charlies crew in München im Überblick: Gassi-Service, Hundesitter & Einzelbetreuung und unsere Hundetagesstätte in Pasing. Voll versichert.",
  canonical: SERVICES_URL,
  og: { type: "website", title: SERVICES_TITLE, description: SERVICES_SOCIAL_DESCRIPTION, url: SERVICES_URL },
  twitter: { title: SERVICES_TITLE, description: SERVICES_SOCIAL_DESCRIPTION },
  jsonLd: {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        name: "Gassi-Service München",
        serviceType: "Hundebetreuung / Dog Walking",
        description: "Premium Gassi-Service in München mit kleinen Gruppen und festen AM/PM-Slots.",
        areaServed: { "@type": "City", name: "München" },
        provider: SERVICE_PROVIDER,
        url: "https://www.charliescrew.com/gassi-service",
      },
      {
        "@type": "Service",
        name: "Hundesitter München",
        serviceType: "Hundesitting / Hundebetreuung",
        description: "Liebevolle Hundebetreuung in München: Hundesitting in kleinen Gruppen, unterwegs in Wald und Park, mit Abholung.",
        areaServed: { "@type": "City", name: "München" },
        provider: SERVICE_PROVIDER,
        url: "https://www.charliescrew.com/hundesitter-muenchen",
      },
      {
        "@type": "Service",
        name: "Hundetagesstätte München-Pasing",
        serviceType: "Doggy Daycare",
        description: "Tagesbetreuung in kleinen Gruppen mit Indoor- und Outdoor-Bereich in München-Pasing.",
        areaServed: { "@type": "City", name: "München" },
        provider: SERVICE_PROVIDER,
        url: "https://www.charliescrew.com/hundetagesstaette",
      },
    ],
  },
};

const ABOUT_URL = "https://www.charliescrew.com/about";
const ABOUT_TITLE = "Über uns: Das Team hinter charlies crew München";
const ABOUT_SOCIAL_DESCRIPTION = "Hundeliebhaber aus München. Lerne das Team hinter charlies crew kennen.";

const about: LpRouteMeta = {
  pfad: "/about",
  title: ABOUT_TITLE,
  description:
    "Lerne das Team hinter charlies crew kennen: Hundeliebhaber, die deinen Hund in München mit Leidenschaft betreuen.",
  canonical: ABOUT_URL,
  og: { type: "website", title: ABOUT_TITLE, description: ABOUT_SOCIAL_DESCRIPTION, url: ABOUT_URL },
  twitter: { title: ABOUT_TITLE, description: ABOUT_SOCIAL_DESCRIPTION },
  jsonLd: {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: ABOUT_TITLE,
    url: ABOUT_URL,
    isPartOf: { "@type": "WebSite", "@id": "https://www.charliescrew.com/#website" },
    about: { "@type": "LocalBusiness", "@id": "https://www.charliescrew.com/#business" },
  },
};

// /schnuppertag: Die A/B-Variante ?v=B läuft unter derselben URL und teilt sich den Head.
const SCHNUPPER_URL = "https://www.charliescrew.com/schnuppertag";
const SCHNUPPER_TITLE = "Schnupper-Telefonat - Hundebetreuung München | charlies crew";
const SCHNUPPER_SOCIAL_DESCRIPTION =
  "Kostenloses Kennenlern-Telefonat, danach erste Ausflüge mit der Einstiegs-Karte.";

const schnuppertag: LpRouteMeta = {
  pfad: "/schnuppertag",
  title: SCHNUPPER_TITLE,
  description:
    "Lerne charlies crew kennen: kostenloses Schnupper-Telefonat, danach erste Ausflüge mit der Einstiegs-Karte. Gassi-Service in kleinen Gruppen, voll versichert.",
  canonical: SCHNUPPER_URL,
  og: { type: "website", title: SCHNUPPER_TITLE, description: SCHNUPPER_SOCIAL_DESCRIPTION, url: SCHNUPPER_URL },
  twitter: { title: SCHNUPPER_TITLE, description: SCHNUPPER_SOCIAL_DESCRIPTION },
  jsonLd: {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: SCHNUPPER_TITLE,
    url: SCHNUPPER_URL,
    isPartOf: { "@type": "WebSite", "@id": "https://www.charliescrew.com/#website" },
  },
};

// /jobs: Das JobPosting-Schema setzt die Seite weiter selbst per Helmet — es rechnet
// datePosted/validThrough aus dem heutigen Datum, und Datum hat hier nichts verloren.
const JOBS_URL = "https://www.charliescrew.com/jobs";
const JOBS_TITLE = "Jobs als Hundebetreuer:in München | charlies crew";
const JOBS_DESCRIPTION =
  "Werde Teil von charlies crew in München: Werkstudent:in, Mini-Job, Teil- oder Vollzeit als Hundebetreuer:in. Faire Bezahlung, kleine Gruppen, professionelles Umfeld.";

const jobs: LpRouteMeta = {
  pfad: "/jobs",
  title: JOBS_TITLE,
  description: JOBS_DESCRIPTION,
  canonical: JOBS_URL,
  og: { type: "website", title: JOBS_TITLE, description: JOBS_DESCRIPTION, url: JOBS_URL },
  twitter: {
    title: JOBS_TITLE,
    description:
      "Werde Teil von charlies crew in München: Werkstudent:in, Mini-Job, Teil- oder Vollzeit als Hundebetreuer:in.",
  },
  jsonLd: {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: JOBS_TITLE,
    description: JOBS_DESCRIPTION,
    url: JOBS_URL,
    isPartOf: { "@type": "WebSite", "@id": "https://www.charliescrew.com/#website" },
  },
};

// /datenschutz: Rechtstext, bewusst noindex,follow (wie /impressum und /konto-loeschen) und
// nicht in der Sitemap. Eigenes Roh-HTML trotzdem, weil Store-Prüfer, Link-Vorschauen und
// Crawler ohne JS sonst den Startseiten-Head ohne noindex sehen (Datenschutz-URL im Store).
const DATENSCHUTZ_URL = "https://www.charliescrew.com/datenschutz";
const DATENSCHUTZ_TITLE = "Datenschutzerklärung | charlies crew";
const DATENSCHUTZ_DESCRIPTION =
  "Wie die Charlies GmbH auf charliescrew.com und in der App charlies crew mit deinen Daten umgeht: Zwecke, Dienstleister, Speicherdauer und deine Rechte.";

const datenschutz: LpRouteMeta = {
  pfad: "/datenschutz",
  title: DATENSCHUTZ_TITLE,
  description: DATENSCHUTZ_DESCRIPTION,
  canonical: DATENSCHUTZ_URL,
  og: { type: "website", title: DATENSCHUTZ_TITLE, description: DATENSCHUTZ_DESCRIPTION, url: DATENSCHUTZ_URL },
  twitter: { title: DATENSCHUTZ_TITLE, description: DATENSCHUTZ_DESCRIPTION },
  robots: "noindex,follow",
  jsonLd: {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: DATENSCHUTZ_TITLE,
    url: DATENSCHUTZ_URL,
    inLanguage: "de-DE",
    dateModified: DATENSCHUTZ_STAND_ISO,
    isPartOf: { "@type": "WebSite", "@id": "https://www.charliescrew.com/#website" },
    publisher: { "@type": "LocalBusiness", "@id": "https://www.charliescrew.com/#business" },
  },
};

// /en: englische Kurzseite (GEO-Maßnahme 12, 12.10.2026). Hier nur der Kopf; die Texte und das
// FAQPage-JSON-LD stehen in src/data/seiteEn.ts, damit sie nicht im Chunk app-shared landen,
// den jede Seite lädt. ENTWURF, Freigabe Noah im PR.
const EN_URL = "https://www.charliescrew.com/en";
const EN_TITLE = "Dog Walking & Dog Daycare in Munich | charlies crew";
const EN_DESCRIPTION =
  "Dog walking with pick-up within the city of Munich and dog daycare in Munich-Pasing. Small groups, fully insured. Book a free intro call.";

const en = {
  pfad: "/en",
  title: EN_TITLE,
  description: EN_DESCRIPTION,
  canonical: EN_URL,
  og: { type: "website", title: EN_TITLE, description: EN_DESCRIPTION, url: EN_URL },
  twitter: { title: EN_TITLE, description: EN_DESCRIPTION },
  lang: "en",
  alternates: HREFLANG_START_EN,
  jsonLd: {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${EN_URL}#webpage`,
    name: EN_TITLE,
    description: EN_DESCRIPTION,
    url: EN_URL,
    inLanguage: "en",
    isPartOf: { "@type": "WebSite", "@id": "https://www.charliescrew.com/#website" },
    about: { "@type": "LocalBusiness", "@id": "https://www.charliescrew.com/#business" },
  },
  // En.tsx gibt genau dieses jsonLd auch per Helmet aus.
  jsonLdVonHelmetUebernommen: true,
} satisfies LpRouteMeta;

/**
 * Blog-Übersicht: bekommt die Artikelliste als Argument (wie blogRouteMeta), weil diese
 * Datei blogPosts.ts nicht selbst importieren soll. Blog.tsx und das Plugin übergeben
 * dieselbe Liste.
 */
export function blogIndexMeta(
  posts: { slug: string; title: string; excerpt: string; date: string; author: string }[],
): LpRouteMeta {
  const url = "https://www.charliescrew.com/blog";
  const title = "charlies crew Blog - Tipps rund um Hund in München";
  const description =
    "Praktische Tipps, spannende Einblicke und Wissenswertes rund um Hundebetreuung, Gassi-Service und das Leben mit Hund in München.";
  return {
    pfad: "/blog",
    title,
    description,
    canonical: url,
    og: { type: "website", title, description, url },
    twitter: { title, description },
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Blog",
      name: "charlies crew Blog",
      url,
      description:
        "Praktische Tipps und Wissenswertes rund um Hundebetreuung, Gassi-Service und das Leben mit Hund in München.",
      publisher: {
        "@type": "Organization",
        "@id": "https://www.charliescrew.com/#business",
        name: "charlies crew",
        logo: "https://www.charliescrew.com/favicon.png",
      },
      blogPost: posts.map((post) => ({
        "@type": "BlogPosting",
        headline: post.title,
        description: post.excerpt,
        datePublished: post.date,
        author: { "@type": "Organization", name: post.author },
        url: `https://www.charliescrew.com/blog/${post.slug}`,
      })),
    },
  };
}

/**
 * Blog-Artikel: dieselbe Quelle wie die Seite selbst (`blogPosts.ts` → BlogLayout),
 * damit Roh-HTML und Helmet nicht auseinanderlaufen können. Das Plugin ruft das hier
 * beim Build für jeden Artikel auf.
 */
export function blogRouteMeta(post: {
  slug: string;
  title: string;
  metaTitle: string;
  metaDescription: string;
  date: string;
}): LpRouteMeta {
  const url = `https://www.charliescrew.com/blog/${post.slug}`;
  return {
    pfad: `/blog/${post.slug}`,
    title: post.metaTitle,
    description: post.metaDescription,
    canonical: url,
    og: { type: "article", title: post.metaTitle, description: post.metaDescription, url },
    twitter: { title: post.metaTitle, description: post.metaDescription },
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: post.title,
      description: post.metaDescription,
      datePublished: post.date,
      url,
      mainEntityOfPage: { "@type": "WebPage", "@id": url },
      publisher: {
        "@type": "Organization",
        "@id": "https://www.charliescrew.com/#business",
        name: "charlies crew",
      },
    },
  };
}

/** Eine Route je Landingpage. Wer hier eine Route ergänzt, bekommt ihre index.html beim Build gratis. */
export const LP_META = {
  "/gassi-service": gassiService,
  "/hundetagesstaette": hundetagesstaette,
  "/preise": preise,
  "/faq": faq,
  "/hundesitter-muenchen": hundesitterMuenchen,
  "/welcher-service": serviceFinder,
  "/services": services,
  "/about": about,
  "/schnuppertag": schnuppertag,
  "/jobs": jobs,
  "/datenschutz": datenschutz,
  "/en": en,
} satisfies Record<string, LpRouteMeta>;
